/**
 * Customer session data (travel periods, guests and currency)
 *
 * @author bgr
 */

// Check for iframe before access history (which would fail in iframe)
import "../../shared/frame-buster";

import { data, session } from "../../shared/state/payload";
import { track } from "../tracker/EventTracker";
import {
	formatDate,
	formatRangeToDE,
	getNumNightsBetween,
	isAfter,
	isSameDay,
} from "../../shared/l10n/date";
import { pageType } from "@escapio/portal/state/pageType";
import { sum } from "../../shared/util/array";
import {
	convertLegacySearchParams,
	parse as parseHash,
	pushState,
	stripHashParams,
} from "@escapio/shared/state/urlHash";

export const MIN_NUM_ADULTS = 1;
export const MAX_NUM_ADULTS = 6;

export const MIN_NUM_CHILDREN = 0;
export const MAX_NUM_CHILDREN = 3;
export const MIN_AGE_CHILDREN = 0;
export const MAX_AGE_CHILDREN = 16;

export const MAX_NUM_ROOMS = 3;
export const MAX_NUM_NIGHTS = 30;

const PARAM_DATES = "dates";
const PARAM_ROOMS = "rooms";
const PARAM_CURRENCY = "currency";
const PARAMS = [PARAM_DATES, PARAM_ROOMS, PARAM_CURRENCY];

const CHILDREN_SEPARATOR = ",";
const GUEST_SEPARATOR = ":";
const ROOM_SEPARATOR = "|";

const NO_DATES = "no-dates";

const now = Date.now();
const { defaultCurrency, defaultRooms } = session;

// TODO: Legacy way for searchParam handling. Replace with 'window.url' when most of the external urls were updated.
const { hash } = convertLegacySearchParams(PARAMS);

export const formatDates = (arrival, departure) =>
	[arrival, departure].map(formatDate.bind(null, "dd.MM.yyyy")).join("-");

const formatRooms = rooms =>
	rooms
		.map(({ adults, children }) =>
			[adults, children.join(CHILDREN_SEPARATOR)]
				.filter(Boolean)
				.join(GUEST_SEPARATOR),
		)
		.join(ROOM_SEPARATOR);

export const formatRequestParameter = (arrival, departure, rooms, currency) => {
	return {
		rooms: formatRooms(rooms),
		currency_id: currency,
		...(arrival && departure
			? {
					dates: formatDates(arrival, departure),
				}
			: {}),
	};
};

let arrival, departure, currency, rooms;
const map = {
	currency: {
		parse: value => value,
		validate: value => Object.keys(data.currencySelector).includes(value),
		defaultValue: defaultCurrency,
	},
	dates: {
		parse: parseDates,
		validate: datesAreValid,
		defaultValue: [null, null],
	},
	rooms: {
		parse: parseRooms,
		validate: rooms => rooms.length <= MAX_NUM_ROOMS && !rooms.includes(null),
		defaultValue: defaultRooms,
	},
};
const FORCE_DEFAULT = Symbol("force_default");
const sources = {
	hash: key => {
		const hashMap = parseHash({ [key]: null })(hash);
		// Allow to reset the dates param, when the param in the hash without a value.
		return hashMap.hasOwnProperty(key) && hashMap[key] === undefined
			? FORCE_DEFAULT
			: hashMap[key];
	},
	localStorage: key => {
		const value = localStorage.getItem(key);
		return key === "dates" && value === NO_DATES
			? map[key].defaultValue
			: value;
	},
	default: key => map[key].defaultValue,
};

/* INITIALIZE: populate travel data fields */
[arrival, departure] = getValue("dates");
rooms = getValue("rooms");
currency = getValue("currency");
saveToSession();
pushState(stripHashParams(hash, PARAMS)); // Remove travel data parameters from current url.

function getValue(key) {
	return Object.values(sources).reduce((previousValue, currentSource) => {
		const { parse, validate, defaultValue } = map[key];
		if (previousValue) {
			return previousValue;
		}
		const value = currentSource(key);
		if (value === FORCE_DEFAULT) {
			return defaultValue;
		}
		if (value === defaultValue) {
			return value;
		}
		const parsed = value ? parse(value) : null;
		return parsed && validate(parsed) ? parsed : null;
	}, undefined);
}

function parseDates(string) {
	return string.split("-").map(dateString => {
		const match = dateString.match(/(\d{2}).(\d{2}).(\d{4})/);
		return match ? new Date(match[3], match[2] - 1, match[1]) : null;
	});
}

function datesAreValid([arrival, departure]) {
	return (
		arrival &&
		departure &&
		(isSameDay(arrival, now) || isAfter(arrival, now)) &&
		isAfter(departure, arrival) &&
		getNumNightsBetween(arrival, departure) <= MAX_NUM_NIGHTS
	);
}

function parseRooms(value) {
	return value.split(ROOM_SEPARATOR).map(room => {
		const [adults, childrenString] = room.split(GUEST_SEPARATOR);
		const children = childrenString ? parseChildren(childrenString) : [];
		return isNaN(adults) ||
			adults < MIN_NUM_ADULTS ||
			adults > MAX_NUM_ADULTS ||
			children.includes(null) ||
			children.length > MAX_NUM_CHILDREN
			? null
			: { adults: parseInt(adults, 10), children };
	});
}

function parseChildren(children) {
	return children
		.split(CHILDREN_SEPARATOR)
		.map(childAge =>
			isNaN(childAge) ||
			childAge < MIN_AGE_CHILDREN ||
			childAge > MAX_AGE_CHILDREN
				? null
				: parseInt(childAge, 10),
		);
}

function cloneRooms(rooms) {
	return [
		...rooms.map(room => ({
			...{ adults: room.adults, children: [...room.children] },
		})),
	];
}

const datesListeners = [];
const guestsListeners = [];
const currencyListeners = [];
const anyListeners = [];

function saveToSession() {
	localStorage.setItem(PARAM_ROOMS, formatRooms(rooms));
	localStorage.setItem(
		PARAM_DATES,
		arrival && departure ? formatDates(arrival, departure) : NO_DATES,
	);
	localStorage.setItem(PARAM_CURRENCY, currency);
}

const getter = {};
Object.defineProperties(getter, {
	arrival: {
		enumerable: true,
		get() {
			return arrival;
		},
	},
	departure: {
		enumerable: true,
		get() {
			return departure;
		},
	},
	hasDates: {
		enumerable: true,
		get() {
			return Boolean(arrival && departure);
		},
	},
	nights: {
		enumerable: true,
		get() {
			return getNumNightsBetween(arrival, departure);
		},
	},
	numAllGuests: {
		enumerable: true,
		get() {
			return this.allAdults + this.allChildren;
		},
	},
	allAdults: {
		enumerable: true,
		get() {
			return sum(rooms.map(({ adults }) => adults));
		},
	},
	allChildren: {
		enumerable: true,
		get() {
			return sum(rooms.map(({ children }) => children.length));
		},
	},
	allChildrenAges: {
		enumerable: true,
		get() {
			return rooms
				.map(({ children }) => children)
				.reduce((acc, curr) => acc.concat(curr), []);
		},
	},
	rooms: {
		enumerable: true,
		get() {
			return cloneRooms(rooms);
		},
	},
	currency: {
		enumerable: true,
		get() {
			return currency;
		},
	},
	formattedRequestParameter: {
		enumerable: true,
		get() {
			return formatRequestParameter(arrival, departure, rooms, currency);
		},
	},
	formattedUrlParameter: {
		enumerable: true,
		get() {
			const formatted = formatRequestParameter(
				arrival,
				departure,
				rooms,
				currency,
			);
			const params = {
				...(formatted.dates ? { [PARAM_DATES]: formatted.dates } : {}),
				[PARAM_ROOMS]: formatted.rooms,
				[PARAM_CURRENCY]: formatted.currency_id,
			};
			return Object.entries(params)
				.map(([param, value]) => `${param}=${value}`)
				.join("&");
		},
	},
});

export const travelData = Object.freeze(getter);

function changeDates(newArrival, newDeparture) {
	arrival = newArrival;
	departure = newDeparture;
	track(
		"Reisedaten",
		"Reisezeitraum",
		`${
			arrival && departure ? formatRangeToDE(arrival, departure) : "Ohne Datum"
		} - ${pageType}`,
	);
}

function changeRooms(newRooms) {
	rooms = cloneRooms(newRooms);
	track("Reisedaten", "Anzahl Zimmer", `${formatRooms(rooms)} - ${pageType}`);
}

export function updateDates(newArrival, newDeparture) {
	changeDates(newArrival, newDeparture);
	saveToSession();

	callListeners(datesListeners, arrival, departure);
	callListeners(anyListeners);
}

export function updateRooms(newRooms) {
	changeRooms(newRooms);
	saveToSession();

	callListeners(guestsListeners, newRooms);
	callListeners(anyListeners);
}

export function updateDatesAndRooms(
	newArrival,
	newDeparture,
	newRooms,
	propagateChange = true,
) {
	changeDates(newArrival, newDeparture);
	changeRooms(newRooms);
	saveToSession();

	if (propagateChange) {
		callListeners(datesListeners, newArrival, newDeparture);
		callListeners(guestsListeners, newRooms);
		callListeners(anyListeners);
	}
}

export function updateCurrency(newCurrency) {
	currency = newCurrency;
	saveToSession();
	callListeners(currencyListeners, currency);
	callListeners(anyListeners);
}

export function onChangeDates(callback) {
	datesListeners.push(callback);
}

export function onChangeGuests(callback) {
	guestsListeners.push(callback);
}

export function onChangeCurrency(callback) {
	currencyListeners.push(callback);
}

export function onChangeAny(callback) {
	anyListeners.push(callback);
}

function callListeners(listeners, ...params) {
	listeners.forEach(listener => listener(...params));
}
